import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  MessageSquare, Globe, Send as SendIcon, Camera, MessageCircle, Loader2,
  Search, Download, AlertTriangle, UserCog, Power, ChevronRight, RefreshCw,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const PLATFORM_META = {
  website: { label: 'Website', color: 'bg-blue-100 text-blue-700 border-blue-200', Icon: Globe },
  facebook: { label: 'Facebook', color: 'bg-[#1877F2]/10 text-[#1877F2] border-[#1877F2]/20', Icon: MessageCircle },
  instagram: { label: 'Instagram', color: 'bg-pink-100 text-pink-700 border-pink-200', Icon: Camera },
  telegram: { label: 'Telegram', color: 'bg-sky-100 text-sky-700 border-sky-200', Icon: SendIcon },
  whatsapp: { label: 'WhatsApp', color: 'bg-green-100 text-green-700 border-green-200', Icon: MessageCircle },
  email: { label: 'Email', color: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Mail },
};

function formatRelative(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export const InteractionsTab = ({ chatbotId, chatbot }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [aiActive, setAiActive] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [moderators, setModerators] = useState([]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (platform && platform !== 'all') params.platform = platform;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        const d = new Date(dateTo);
        d.setHours(23, 59, 59, 999);
        params.date_to = d.toISOString();
      }
      if (search.trim()) params.search = search.trim();
      if (unreadOnly) params.unread_only = true;
      const { data } = await api.get(`/chatbots/${chatbotId}/interactions`, { params });
      setItems(data.interactions || []);
      setTotal(data.total || 0);
      setAiActive(!data.ai_globally_disabled);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load interactions');
    } finally {
      setLoading(false);
    }
  }, [chatbotId, page, pageSize, platform, dateFrom, dateTo, search, unreadOnly]);

  useEffect(() => { loadList(); }, [loadList]);

  // Load moderators (owner + invited) for the assignment dropdown
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/chatbots/${chatbotId}/moderators`);
        const owner = chatbot?.user_id
          ? [{ user_id: chatbot.user_id, name: chatbot.user_name || 'Account Owner', role: 'owner' }]
          : [];
        setModerators([...owner, ...(data || []).map(m => ({ ...m, role: 'moderator' }))]);
      } catch {
        setModerators(chatbot?.user_id
          ? [{ user_id: chatbot.user_id, name: 'Account Owner', role: 'owner' }]
          : []);
      }
    })();
  }, [chatbotId, chatbot?.user_id, chatbot?.user_name]);

  const toggleGlobalAI = async (next) => {
    try {
      await api.post(`/chatbots/${chatbotId}/ai-active`, { ai_active: next });
      setAiActive(next);
      toast.success(next ? 'AI auto-reply enabled' : 'AI auto-reply paused for all channels');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to toggle AI');
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (platform && platform !== 'all') params.set('platform', platform);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) {
        const d = new Date(dateTo);
        d.setHours(23, 59, 59, 999);
        params.set('date_to', d.toISOString());
      }
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/chatbots/${chatbotId}/interactions/export.csv?${params}`;
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Failed to export CSV');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4" data-testid="interactions-tab">
      {/* Global AI kill-switch */}
      <Card className={`border ${aiActive ? 'bg-white border-slate-200' : 'bg-amber-50 border-amber-300'}`}>
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${aiActive ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <Power className={`w-5 h-5 ${aiActive ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <p className="font-semibold text-slate-900">
                AI auto-reply is {aiActive ? 'ON' : 'PAUSED'} for all channels
              </p>
              <p className="text-xs text-slate-500">
                {aiActive
                  ? 'Your AI Employee replies automatically to new incoming messages everywhere.'
                  : 'Incoming messages are queued for human moderators. The AI will not respond until you re-enable.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Pause AI</span>
            <Switch
              checked={!aiActive}
              onCheckedChange={(v) => toggleGlobalAI(!v)}
              data-testid="global-ai-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Filter bar */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-xs text-slate-500 uppercase tracking-wide">Search</label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Contact, message or ID…"
                className="pl-9"
                data-testid="interactions-search"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 uppercase tracking-wide">Platform</label>
            <Select value={platform} onValueChange={(v) => { setPlatform(v); setPage(1); }}>
              <SelectTrigger data-testid="interactions-platform-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All platforms</SelectItem>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="telegram">Telegram</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 uppercase tracking-wide">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} data-testid="interactions-date-from" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 uppercase tracking-wide">To</label>
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} data-testid="interactions-date-to" />
          </div>
          <div className="md:col-span-2 flex items-center justify-between md:justify-end gap-2 pb-1">
            <label className="text-xs text-slate-500 flex items-center gap-2">
              <Switch checked={unreadOnly} onCheckedChange={(v) => { setUnreadOnly(v); setPage(1); }} data-testid="interactions-unread-toggle" />
              Unread only
            </label>
          </div>
          <div className="md:col-span-12 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={loadList} disabled={loading} data-testid="interactions-refresh">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="interactions-export-csv">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-slate-900 flex items-center gap-2 text-base">
            <MessageSquare className="w-5 h-5 text-blue-600" /> Conversations
            <span className="text-xs font-normal text-slate-500">({total})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading conversations…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-700 font-medium">No conversations yet</p>
              <p className="text-sm text-slate-500 mt-1">When customers chat with this AI Employee, the conversations will appear here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="interactions-list">
              {items.map((i) => {
                const meta = PLATFORM_META[i.platform] || PLATFORM_META.website;
                const PIcon = meta.Icon;
                return (
                  <li
                    key={i.interaction_id}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                    onClick={() => setOpenId(i.interaction_id)}
                    data-testid={`interaction-row-${i.platform}`}
                  >
                    <div className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 ${meta.color}`}>
                      <PIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">
                          {i.contact_name || i.contact_id}
                        </span>
                        <Badge variant="outline" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                        {i.human_takeover && (
                          <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">Takeover ON</Badge>
                        )}
                        {i.assigned_to_name && (
                          <Badge className="text-[10px] bg-violet-100 text-violet-700 border-violet-200">
                            <UserCog className="w-3 h-3 mr-1" /> {i.assigned_to_name}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 truncate mt-0.5">
                        <span className="text-slate-400 text-xs mr-1">
                          {i.last_message_by === 'human' ? 'You:' : i.last_message_by === 'ai' ? 'AI:' : ''}
                        </span>
                        {i.last_message || <em className="text-slate-400">No messages</em>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-slate-500">{formatRelative(i.last_message_at)}</p>
                      <div className="flex items-center justify-end gap-1.5 mt-1">
                        {i.unread_count > 0 && (
                          <Badge className="bg-rose-600 text-white border-0 text-[10px] h-5 px-1.5 min-w-[20px] justify-center" data-testid="interaction-unread-badge">
                            {i.unread_count}
                          </Badge>
                        )}
                        <span className="text-[10px] text-slate-400">{i.message_count} msg</span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">Page {page} of {totalPages} · {total} total</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {openId && (
        <InteractionThreadDrawer
          chatbotId={chatbotId}
          interactionId={openId}
          moderators={moderators}
          onClose={() => { setOpenId(null); loadList(); }}
        />
      )}
    </div>
  );
};

// ---------------- Thread drawer ----------------

const InteractionThreadDrawer = ({ chatbotId, interactionId, moderators, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chatbots/${chatbotId}/interactions/${encodeURIComponent(interactionId)}`);
      setDetail(data);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [chatbotId, interactionId]);

  useEffect(() => { load(); }, [load]);

  // Real-time updates via SSE — pushes new user/AI/human messages while the
  // drawer is open so moderators don't have to refresh.
  useEffect(() => {
    if (!chatbotId || !interactionId) return undefined;
    // Reuse the session token from localStorage (EventSource cannot send
    // cookies cross-origin in some browsers, so we pass it as ?token=).
    let token = '';
    try {
      token = localStorage.getItem('session_token') || '';
    } catch {
      token = '';
    }
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/chatbots/${chatbotId}/interactions/${encodeURIComponent(interactionId)}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const es = new EventSource(url, { withCredentials: true });

    es.addEventListener('message', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type !== 'message') return;
        setDetail(prev => {
          if (!prev) return prev;
          // De-dupe: skip if this exact (role, content, created_at) is already there.
          const tail = prev.messages.slice(-3);
          if (tail.some(m => m.role === data.role && m.content === data.content && m.created_at === data.created_at)) {
            return prev;
          }
          const next = {
            ...prev,
            messages: [...prev.messages, {
              role: data.role,
              content: data.content,
              created_at: data.created_at,
              sender_name: data.sender_name,
            }],
          };
          // Scroll to bottom on next paint.
          setTimeout(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }, 50);
          return next;
        });
      } catch {
        /* ignore malformed events */
      }
    });

    es.onerror = () => {
      // EventSource auto-reconnects with the `retry:` hint from the server.
      // No-op; only log in dev.
    };

    return () => { es.close(); };
  }, [chatbotId, interactionId]);

  const toggleTakeover = async (enabled) => {
    try {
      await api.post(
        `/chatbots/${chatbotId}/interactions/${encodeURIComponent(interactionId)}/takeover`,
        { enabled },
      );
      setDetail(d => ({ ...d, human_takeover: enabled }));
      toast.success(enabled ? 'You are now handling this conversation' : 'AI will resume auto-reply');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update takeover');
    }
  };

  const assign = async (userId) => {
    const next = userId === '__unassign__' ? null : userId;
    try {
      const { data } = await api.post(
        `/chatbots/${chatbotId}/interactions/${encodeURIComponent(interactionId)}/assign`,
        { user_id: next },
      );
      setDetail(d => ({ ...d, assigned_to: next, assigned_to_name: data.assigned_to_name }));
      toast.success(next ? `Assigned to ${data.assigned_to_name || 'user'}` : 'Unassigned');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to assign');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/chatbots/${chatbotId}/interactions/${encodeURIComponent(interactionId)}/reply`,
        { message: replyText.trim() },
      );
      if (data?.delivered === false) {
        toast.error(`Saved but delivery failed: ${data.error || 'unknown'}`);
      } else {
        toast.success('Reply sent');
      }
      setReplyText('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const meta = detail ? (PLATFORM_META[detail.platform] || PLATFORM_META.website) : null;
  const PIcon = meta?.Icon;

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0" data-testid="interaction-drawer">
        <SheetHeader className="px-5 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {meta && (
                <div className={`w-10 h-10 rounded-full border flex items-center justify-center ${meta.color} shrink-0`}>
                  <PIcon className="w-4 h-4" />
                </div>
              )}
              <div className="min-w-0">
                <SheetTitle className="text-base truncate">
                  {detail?.contact_name || detail?.contact_id || 'Loading…'}
                </SheetTitle>
                <p className="text-xs text-slate-500 truncate">
                  {meta?.label}{detail?.contact_id ? ` · ${detail.contact_id}` : ''}
                </p>
              </div>
            </div>
          </div>
          {detail && (
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <Switch
                  checked={detail.human_takeover}
                  onCheckedChange={toggleTakeover}
                  data-testid="takeover-toggle"
                />
                <span>Full takeover (AI off)</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <UserCog className="w-4 h-4 text-slate-400" />
                <Select
                  value={detail.assigned_to || '__unassign__'}
                  onValueChange={assign}
                >
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="assign-select">
                    <SelectValue placeholder="Assign…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassign__">Unassigned</SelectItem>
                    {moderators.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.name || m.email} {m.role === 'owner' ? '(owner)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {detail?.human_takeover && (
            <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>You&apos;re fully handling this conversation. AI auto-reply is OFF until you toggle it back.</span>
            </div>
          )}
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50 space-y-3" data-testid="interaction-messages">
          {loading ? (
            <div className="text-center text-slate-500 py-8"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
          ) : detail?.messages.length ? detail.messages.map((m, idx) => {
            const isUser = m.role === 'user';
            const isHuman = m.role === 'human';
            return (
              <div key={m.message_id || idx} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                  isUser
                    ? 'bg-white text-slate-900 border border-slate-200 rounded-bl-sm'
                    : isHuman
                    ? 'bg-emerald-600 text-white rounded-br-sm'
                    : 'bg-blue-600 text-white rounded-br-sm'
                }`}>
                  {!isUser && (
                    <p className="text-[10px] uppercase tracking-wide opacity-80 mb-0.5">
                      {isHuman ? (m.sender_name || 'Moderator') : 'AI'}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  <p className={`text-[10px] mt-1 ${isUser ? 'text-slate-400' : 'text-white/70'}`}>
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          }) : (
            <div className="text-center text-slate-500 py-8">No messages in this conversation yet.</div>
          )}
        </div>

        <div className="border-t border-slate-200 p-3 bg-white">
          <div className="flex items-end gap-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
              rows={2}
              placeholder={
                detail?.human_takeover
                  ? 'Reply as moderator (AI is paused for this conversation)…'
                  : 'Reply — AI will yield to you for a few minutes after each message…'
              }
              className="flex-1 resize-none border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="reply-input"
            />
            <Button
              onClick={sendReply}
              disabled={sending || !replyText.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-reply-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendIcon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InteractionsTab;
